import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

const RESTORE_TIMEOUT_MS = 3000
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const

const scrollPositions = new Map<string, number>()

function getMaxScrollY(): number {
  return document.documentElement.scrollHeight - window.innerHeight
}

// A page can still grow after it mounts (a table or the agenda fetching in place), so the saved
// position may be out of reach until that content renders. Keep trying each frame until the page is tall enough, the user scrolls, or time runs out.
function restoreScrollY(top: number): () => void {
  const deadline = Date.now() + RESTORE_TIMEOUT_MS
  let frame = 0
  const stop = () => {
    window.cancelAnimationFrame(frame)
    USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop))
  }
  const attempt = () => {
    if (getMaxScrollY() >= top || Date.now() >= deadline) {
      window.scrollTo(0, top)
      stop()
      return
    }
    frame = window.requestAnimationFrame(attempt)
  }
  USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }))
  attempt()
  return stop
}

/**
 * The browser's own restoration runs on popstate, before the remounted page has its content,
 * so it clamps every position to a short page. Each history entry's position is recorded as the user
 * scrolls and put back on back/forward once the page is tall enough; a new screen starts at the top.
 */
export function useScrollRestoration() {
  const location = useLocation()
  const navigationType = useNavigationType()
  const currentKey = useRef(location.key)
  const previousUrl = useRef(location.pathname + location.search)

  useEffect(() => {
    window.history.scrollRestoration = 'manual'
    const savePosition = () => scrollPositions.set(currentKey.current, window.scrollY)
    window.addEventListener('scroll', savePosition, { passive: true })
    return () => {
      window.removeEventListener('scroll', savePosition)
      window.history.scrollRestoration = 'auto'
    }
  }, [])

  useLayoutEffect(() => {
    currentKey.current = location.key
    const url = location.pathname + location.search
    const isNewUrl = previousUrl.current !== url
    previousUrl.current = url
    const savedTop = scrollPositions.get(location.key)
    if (navigationType === 'POP' && savedTop !== undefined) return restoreScrollY(savedTop)
    if (navigationType === 'PUSH' && isNewUrl && !location.hash) window.scrollTo(0, 0)
    scrollPositions.set(location.key, window.scrollY)
  }, [location.key])
}
